import { FilterOperator } from "../constants/filterOperators";
import { IMfilesPropertyFilter, IOwnPropertyFilter, isOwnPropertyFilter, IFilter, FilterGroupConditionEnum, IFilterGroup } from "../interfaces/filters/IFilter";
import { PropertyDataType } from "../constants/datatypes";
import IMFilesPropertyDefinition from "../interfaces/mfiles/IMFilesPropertyDefinition";
import { IItem } from "../interfaces/IItem";
import { getProperty } from "./itemHelpers";
import { isValid, startOfDay, parseISO } from "date-fns";
import IMfilesValueListDefinition from "../interfaces/mfiles/IMFilesValueListDefinition";
import { IOwnPropertyDefinition } from "../interfaces/filters/IOwnPropertyDefinition";
import { workflowStatesForFiltersSelector } from "../../rematch/selectors/selectors";
import { iRootState } from "../../rematch/store";
import isString from "lodash/isString";
import { IEditedFilter } from "src/gantt/components/filters/FilterPanelContent";
import { uniqueId } from "lodash";

export const createFilter = (
  name: string,
  mfilesPropertyIdOrOwnPropertyName: string | number,
  operator: FilterOperator,
  value: string,
  ownerId?: number,
): IFilter => {
  if (isString(mfilesPropertyIdOrOwnPropertyName)) {
    return {
      name: name,
      operator: operator,
      ownPropertyName: mfilesPropertyIdOrOwnPropertyName,
      value: value,
      ownerId: ownerId,
    };
  } else {
    return {
      name: name,
      operator: operator,
      propertyId: mfilesPropertyIdOrOwnPropertyName,
      value: value,
      ownerId: ownerId,
    };
  }
};


export const createFilterGroup = (
  viewIndex: number,
  key: string,
  name: string,
  conditionType: FilterGroupConditionEnum,
  filters: IEditedFilter[],
  ownerId?: number,
): IFilterGroup => {

  const filtersArray = [];
  
  for (let filter of filters ) {
    filtersArray.push( 
      createFilter(
        uniqueId(`filter_${viewIndex}_`),
        filter.mfilesPropertyIdOrOwnPropertyName,
        filter.operator,
        filter.values.join(","),
        ownerId
      )
    )
  }

  return {
    key: key,
    name: name,
    conditionType: conditionType,
    filters: filtersArray,
    ownerId: ownerId,
  };
};

export const isValidFilter = (filter: IMfilesPropertyFilter, propertyDataType: PropertyDataType): boolean => {
  if (filter.propertyId == null || filter.operator == null) {
    return false;
  }
  if (validFilterTypesForDataTypes.get(propertyDataType)?.includes(filter.operator) === false) {
    return false;
  }
  return true;
};


export const validFilterTypesForDataTypes: Map<PropertyDataType, Array<FilterOperator>> = new Map([
  [
    PropertyDataType.Text,
    [FilterOperator.Equal, FilterOperator.NotEqual, FilterOperator.Like, FilterOperator.EmptyOrNull, FilterOperator.NotEmptyOrNull],
  ],
  [
    PropertyDataType.MultilineText,
    [FilterOperator.Equal, FilterOperator.NotEqual, FilterOperator.Like, FilterOperator.EmptyOrNull, FilterOperator.NotEmptyOrNull],
  ],
  [PropertyDataType.Boolean, [FilterOperator.Equal, FilterOperator.NotEqual, FilterOperator.EmptyOrNull, FilterOperator.NotEmptyOrNull]],
  [
    PropertyDataType.Date,
    [
      // Check only year, month and day!
      FilterOperator.Equal,
      FilterOperator.GreaterThan,
      FilterOperator.GreaterThanOrEqual,
      FilterOperator.LessThan,
      FilterOperator.LessThanOrEqual,
      FilterOperator.EmptyOrNull,
      FilterOperator.NotEmptyOrNull,
      FilterOperator.NotEqual,
    ],
  ],
  [
    PropertyDataType.Floating,
    [
      FilterOperator.Equal,
      FilterOperator.NotEqual,
      FilterOperator.GreaterThanOrEqual,
      FilterOperator.GreaterThan,
      FilterOperator.LessThan,
      FilterOperator.LessThanOrEqual,
      FilterOperator.EmptyOrNull,
      FilterOperator.NotEmptyOrNull,
    ],
  ],
  [
    PropertyDataType.Integer,
    [
      FilterOperator.Equal,
      FilterOperator.NotEqual,
      FilterOperator.GreaterThanOrEqual,
      FilterOperator.GreaterThan,
      FilterOperator.LessThan,
      FilterOperator.LessThanOrEqual,
      FilterOperator.EmptyOrNull,
      FilterOperator.NotEmptyOrNull,
    ],
  ],
  [
    PropertyDataType.Integer64,
    [
      FilterOperator.Equal,
      FilterOperator.NotEqual,
      FilterOperator.GreaterThanOrEqual,
      FilterOperator.GreaterThan,
      FilterOperator.LessThan,
      FilterOperator.LessThanOrEqual,
      FilterOperator.EmptyOrNull,
      FilterOperator.NotEmptyOrNull,
    ],
  ],
  [PropertyDataType.ACL, [FilterOperator.NotEmptyOrNull, FilterOperator.EmptyOrNull]],
  [PropertyDataType.Uninitialized, []],
  [PropertyDataType.FILETIME, [FilterOperator.NotEmptyOrNull, FilterOperator.EmptyOrNull]],
  [PropertyDataType.Time, [FilterOperator.NotEmptyOrNull, FilterOperator.EmptyOrNull]],
  [PropertyDataType.Timestamp, [FilterOperator.NotEmptyOrNull, FilterOperator.EmptyOrNull]],
  [
    PropertyDataType.Lookup,
    [
      FilterOperator.NotEmptyOrNull,
      FilterOperator.EmptyOrNull,
      FilterOperator.Equal,
      FilterOperator.NotEqual,
      FilterOperator.OneOf,
      FilterOperator.NotOneOf,
    ],
  ],
  [
    PropertyDataType.MultiSelectLookup,
    [
      FilterOperator.NotEmptyOrNull,
      FilterOperator.EmptyOrNull,
      FilterOperator.OneOf,
      FilterOperator.NotOneOf,
      FilterOperator.Equal,
      FilterOperator.NotEqual,
    ],
  ],
]);

//Translated in FilterOperatorList.ts
export const operatorToDisplayValue: Map<FilterOperator, string> = new Map([
  [FilterOperator.Equal, "filteroperator.equal"],
  [FilterOperator.NotEqual, "filteroperator.notequal"],
  [FilterOperator.GreaterThan, "filteroperator.greaterthan"],
  [FilterOperator.GreaterThanOrEqual, "filteroperator.greaterthanorequal"],
  [FilterOperator.LessThan, "filteroperator.lessthan"],
  [FilterOperator.LessThanOrEqual, "filteroperator.lessthanorequal"],
  [FilterOperator.Like, "filteroperator.like"],
  [FilterOperator.OneOf, "filteroperator.oneof"],
  [FilterOperator.EmptyOrNull, "filteroperator.emptyornull"],
  [FilterOperator.NotEmptyOrNull, "filteroperator.notemptyornull"],
  [FilterOperator.NotOneOf, "filteroperator.notoneof"],
]);

//Translated in FilterPropertyList.ts
export const ownPropertiesToFilter: IOwnPropertyDefinition[] = [
  { ownPropertyName: "isCheckedOut", dataType: PropertyDataType.Boolean, displayName: "property.ischeckedout" },
  { ownPropertyName: "isCheckedOutToThisUser", dataType: PropertyDataType.Boolean, displayName: "property.ischeckedouttothisuser" },
  {
    ownPropertyName: "status",
    dataType: PropertyDataType.Lookup,
    displayName: "property.status",
    getValues: (state: iRootState) => workflowStatesForFiltersSelector(state as never),
  },
];

export const isValueList = (
  propertyDefinition: IMFilesPropertyDefinition,
  valueListDefinitions: Map<number, IMfilesValueListDefinition>
): boolean => {
  return (
    propertyDefinition?.BasedOnValueList &&
    propertyDefinition?.ValueList &&
    valueListDefinitions.get(propertyDefinition.ValueList)?.RealObjectType !== true
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const filterAgainstPossibleMultiValue = (actualValue, filterValue, filterValueIsArray: boolean): boolean => {
  let actualValues;
  let filterValues;

  //Populate actual value array with an array-type input, or splittable value list, or a single-type value
  if (!actualValue) {
    actualValues = [];
  } else if (Array.isArray(actualValue)) {
    actualValues = actualValue;
  } else if (typeof actualValue === "string" && actualValue.split(",").length > 1) {
    actualValues = actualValue.split(",");
  } else {
    actualValues = [actualValue];
  }

  //Populate filter value array with an array-type parameter, splittable value list, or a single-type value
  if (!filterValue) {
    filterValues = []
  } else if (Array.isArray(filterValue)) {
    filterValues = filterValue;
  } else if (filterValueIsArray && typeof filterValue === "string" && filterValue.split(",").length > 1) {
    filterValues = filterValue.split(",");
  } else {
    filterValues = [filterValue];
  }
  return actualValues.some(v => filterValues.includes(v));
};

const filteringFunctions = new Map<FilterOperator, (actualValue, filterValue) => boolean>([
  [FilterOperator.Equal, (actualValue, filterValue) => filterAgainstPossibleMultiValue(actualValue,filterValue, false)],
  [FilterOperator.NotEqual, (actualValue, filterValue) => filterAgainstPossibleMultiValue(actualValue,filterValue, false) === false],
  [FilterOperator.EmptyOrNull, (actualValue, _) => actualValue == null || actualValue === ""],
  [FilterOperator.NotEmptyOrNull, (actualValue, _) => actualValue != null && actualValue !== ""],
  [FilterOperator.GreaterThan, (actualValue, filterValue) => actualValue > filterValue],
  [FilterOperator.GreaterThanOrEqual, (actualValue, filterValue) => actualValue >= filterValue],
  [FilterOperator.LessThan, (actualValue, filterValue) => actualValue < filterValue],
  [FilterOperator.LessThanOrEqual, (actualValue, filterValue) => actualValue <= filterValue],
  [FilterOperator.Like, (actualValue, filterValue) => actualValue?.includes(filterValue)],
  [FilterOperator.OneOf, (actualValue, filterValue) => filterAgainstPossibleMultiValue(actualValue,filterValue, true)],
  [FilterOperator.NotOneOf, (actualValue, filterValue) => filterAgainstPossibleMultiValue(actualValue,filterValue, true) === false],
]);

export const quickFilterItem = (item: IItem, filter: IMfilesPropertyFilter | IOwnPropertyFilter): boolean => {
  const isOwn = isOwnPropertyFilter(filter);
  let actualValue: string = null;
  if (isOwn) {
    const ownFilter = filter as IOwnPropertyFilter;
    actualValue = item[ownFilter.ownPropertyName]?.toString()?.toLowerCase() ?? "";
  } else {
    const property = getProperty(item.properties, (filter as IMfilesPropertyFilter).propertyId);
    actualValue = property?.displayValue?.toLowerCase() ?? "";
  }
  return actualValue.includes(filter.value.toLowerCase());
};

const filterPropertyValue = (filter: IMfilesPropertyFilter | IOwnPropertyFilter, dataType: PropertyDataType, actualValue: string) => {
  switch (dataType) {
    case PropertyDataType.ACL:
    case PropertyDataType.Boolean:
    case PropertyDataType.FILETIME:
    case PropertyDataType.Lookup:
    case PropertyDataType.MultiSelectLookup:
    case PropertyDataType.Time:
    case PropertyDataType.Timestamp:
    case PropertyDataType.Uninitialized:
      return filteringFunctions.get(filter.operator)(actualValue, filter.value);

    case PropertyDataType.MultilineText:
    case PropertyDataType.Text:
      return filteringFunctions.get(filter.operator)(actualValue?.toLowerCase(), filter.value?.toLowerCase());

    case PropertyDataType.Integer:
    case PropertyDataType.Integer64: {
      const propertyValueInteger = parseInt(actualValue);
      const filterValueInteger = parseInt(filter.value);
      if (isNaN(propertyValueInteger) || isNaN(filterValueInteger)) {
        return false;
      }
      return filteringFunctions.get(filter.operator)(propertyValueInteger, filterValueInteger);
    }
    case PropertyDataType.Floating: {
      const propertyValueFloat = parseFloat(actualValue);
      const filterValueFloat = parseFloat(filter.value);
      if (isNaN(propertyValueFloat) || isNaN(filterValueFloat)) {
        return false;
      }
      return filteringFunctions.get(filter.operator)(propertyValueFloat, filterValueFloat);
    }
    case PropertyDataType.Date: {
      const propertyValueDate = parseISO(actualValue);
      const filterValueDate = parseISO(filter.value);
      if (!propertyValueDate || !isValid(propertyValueDate) || !filterValueDate || !isValid(filterValueDate)) {
        return false;
      }

      // When datatype is Date, compare only date parts (year, month, day) -> use startOfDay()
      // Establishing date equality in js doesn't allow d1 === d2, rather we must use getTime()
      const preparedPropertyValueDate = startOfDay(propertyValueDate).getTime();
      const preparedFilterValueDate = startOfDay(filterValueDate).getTime();

      return filteringFunctions.get(filter.operator)(preparedPropertyValueDate, preparedFilterValueDate);
    }
    default:
      break;
  }

  return true;
}

export const filterItem = (item: IItem, filter: IMfilesPropertyFilter | IOwnPropertyFilter): boolean => {
  const isOwn = isOwnPropertyFilter(filter);
  let actualValue: string = null;
  let dataType: PropertyDataType = null;

  if (isOwn) {
    const ownFilter = filter as IOwnPropertyFilter;
    actualValue = item[ownFilter.ownPropertyName]?.toString();
    dataType = ownPropertiesToFilter.find((p) => p.ownPropertyName === ownFilter.ownPropertyName)?.dataType || PropertyDataType.Text;
  } else {
    const property = getProperty(item.properties, (filter as IMfilesPropertyFilter).propertyId);
    actualValue = property?.value;
    dataType = property?.dataType;
  }

  if (actualValue == null && dataType == null) {
    // Property doesn't actually exist on item, but we'll pretend it does and the value is null.
    return filter.operator === FilterOperator.EmptyOrNull;
  }

  if (filter.operator === FilterOperator.EmptyOrNull || filter.operator === FilterOperator.NotEmptyOrNull) {
    return filteringFunctions.get(filter.operator)(actualValue, filter.value);
  }

  return filterPropertyValue(filter, dataType, actualValue);
};
